<template>
  <form name="login-form" class="login-form" @submit.prevent="handleLogin">
    <div class="login-form__group">
      <label class="login-form__group-label" for="username">
        Username
      </label>
      <input
        class="login-form__group-input"
        type="text"
        id="username"
        autocomplete="username"
        placeholder="Email Address *"
        v-model="username"
        required />
    </div>
    <div class="login-form__group">
      <label class="login-form__group-label" for="password">Password</label>
      <div class="login-form__password-wrapper">
        <input
          class="login-form__group-input"
          :type="showPassword ? 'text' : 'password'"
          id="password"
          autocomplete="current-password"
          placeholder="Password *"
          v-model="password"
          required
        />
        <button
            type="button"
            class="login-form__toggle-password"
            @click="togglePassword"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
        >
          <IconEye v-if="!showPassword" class="login-form__toggle-icon" />
          <IconEyeClosed v-else class="login-form__toggle-icon" />
        </button>
      </div>
    </div>
    <button class="login-form__button" type="submit">
      Login
    </button>

    <nuxt-link class="login-form__link" to="/lost-password">
      Forgot Password?
</nuxt-link>
  </form>

  <div v-if="message" :class="['login__message', messageType]">
    {{ message }}
  </div>
</template>

<script lang="ts">
import { getRecaptchaToken, loadRecaptcha, unmountRecaptcha } from "~/composables/grecaptcha";
import { loginHandler } from "~/utils/session";
import {defineAsyncComponent, navigateTo, useRoute, useRuntimeConfig} from "#imports";

const IconEye = defineAsyncComponent(() => import('~/assets/icons/IconEye.svg'));
const IconEyeClosed = defineAsyncComponent(() => import('~/assets/icons/IconEyeClosed.svg'));

export default {
  setup() {
    const route = useRoute();
    const config = useRuntimeConfig();
    
    // This is our default redirect URL after successful login
    let redirectUrl = '/my-account/';
    
    // Check if we have a redirect_to query parameter, i.e. from the Loader
    // Get redirect URL from query parameter
    if (route.query.redirect_to) {
      const rawRedirectUrl = decodeURIComponent(route.query.redirect_to as string);
      const baseUrl = config.public.baseUrl || '';
      console.log("rawRedirectUrl", rawRedirectUrl);
      console.log("baseUrl", baseUrl);

      // Check if redirect URL starts with the same base URL
      if (rawRedirectUrl.startsWith(baseUrl)) {
        // For OAuth or keycloak URLs, keep the full URL even if it's same domain
        if (rawRedirectUrl.includes('/oauth/') || rawRedirectUrl.includes('keycloak')) {
          redirectUrl = rawRedirectUrl;
        } else {
          // Remove base URL to make it a relative path for regular same-site URLs
          redirectUrl = rawRedirectUrl.replace(baseUrl, '') || '/';
        }
      } else if (rawRedirectUrl.startsWith('/')) {
        // Already a relative path, use as is
        redirectUrl = rawRedirectUrl;
      }

      console.log("redirectUrl", redirectUrl);
      // If it's an external URL (doesn't start with baseUrl or /), ignore it and use default
    }
    
    return {
      redirectUrl
    };
  },
  emits: [
    'login-successful'
  ],
  props: {
    emitEvent: Boolean,
  },
  components: {
    IconEye,
    IconEyeClosed,
  },
  data () {
    return {
      username: '',
      password: '',
      message: null,
      messageType: '',
      showPassword: false,
    };
  },
  mounted() {
    loadRecaptcha();
  },
  unmounted() {
    unmountRecaptcha()
  },
  methods: {
    async handleLogin() {
      try {
        this.resetMessage()

        const token = await getRecaptchaToken();

        if (!token) {
          throw new Error('reCAPTCHA validation failed');
        }

        const result = await loginHandler({ username: this.username, password: this.password, captchaToken: token });

        if (result) {
          if (this.emitEvent) {
            this.$emit('login-successful');
          } else {
            await navigateTo(this.redirectUrl, { external: true });
          }
          return;
        }

        throw new Error('Login failed');
      } catch (error) {
        this.message = error.message || 'There was an error. Please try again or contact support.';
        this.messageType = 'error';
      }
    },
    resetMessage() {
      this.message = ''
      this.messageType = ''
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    }
  },
};
</script>

<style lang="scss">
@use "~/common/scss/form";

.login-form {
  @include form.baseForm('__');

  &__password-wrapper {
    position: relative;
  }

  &__toggle-password {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #333;
    }

    &:focus {
      outline: none;
    }
  }

  &__toggle-icon {
    width: 75%;
    height: 75%;
    pointer-events: none;
  }

  &__group-input[type="password"],
  &__group-input[type="text"] {
    padding-right: 2.5rem;
  }
}
</style>
